import React, { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import Stack from "@mui/material/Stack";
import { Auth } from "aws-amplify";
import { addProducts } from "../api/products";
import jwt_decode from "jwt-decode";

import * as XLSX from "xlsx";
import { decodeReadableStream } from "api/util";
import { Button, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../store/reducers/authSlice";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles({
  button: {
    margin: "0 10px",
  },
  typography: {
    margin: "10px",
  },
});

const containerStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  // height: '100vh'
};

const divStyle = {
  margin: "30px",
  padding: "20px",
  height: "200px",
  width: "600px",
  border: "2px dashed #ccc",
};

const ExcelReader = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [response, setResponse] = useState(null);
  const [items, setItems] = useState([]);
  const [idToken, setIdToken] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);

  useEffect(() => {
    async function getSession() {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      const refreshToken = session.getRefreshToken().getToken();
      dispatch(loginSuccess({ idToken, accessToken, refreshToken }));
      setIdToken(idToken);
      setAccessToken(accessToken);
    }
    getSession();
  }, []);

  const handleSaveProductsClick = async () => {
    setLoading(true);
    try {
      if (idToken) {
        const result = await addProducts(items);
        setResponse(result?.message);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleFileUpload = e => {
    setLoadingFile(true);
    const file = e.target.files[0];
    processFile(file);
    setLoadingFile(false);
  };

  const handleDrop = files => {
    const file = files[0];
    processFile(file);
  };

  const processFile = file => {
    const reader = new FileReader();
    reader.onload = event => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      // Read the 'Product' worksheet
      const productSheet = workbook.Sheets["Products"];
      const productData = XLSX.utils.sheet_to_json(productSheet, { header: 1 });
      const productHeaders = productData[0];
      const products = productData.slice(1).map(row => {
        if (!isNaN(row[0]) && +row[0] !== 0) {
          const product = {};
          row.forEach((cell, index) => {
            let key;
            switch (productHeaders[index]) {
              case "0":
                key = "ProductID";
                break;
              case "1":
                key = "ProductName";
                break;
              case "2":
                key = "ExternalProductId";
                break;
              case "ProductType":
                key = "ProductType";
                break;
              case "ProductDescription":
                key = "ProductDescription";
                break;
              case "ProductDetails":
                key = "ProductDetails";
                break;
              case "SKU":
                key = "SKU";
                break;
              case "Brand Name":
                key = "BrandName";
                break;
              case "Quantity":
                key = "Quantity";
                break;
              case "Price":
                key = "Price";
                break;
              case "Weight(kg)":
                key = "Weightkg";
                break;
              case "Length(cm)":
                key = "Lengthcm";
                break;
              case "Width(cm)":
                key = "Widthcm";
                break;
              case "Height(cm)":
                key = "Heightcm";
                break;
              case "MainImageUrl":
                key = "MainImageUrl";
                break;
              default:
                key = productHeaders[index];
            }
            product[key] = cell;
          });
          return product;
        }
      });

      // Read the 'ImageUrls' worksheet
      const imageSheet = workbook.Sheets["ImageUrls"];
      const imageData = XLSX.utils.sheet_to_json(imageSheet, { header: 1 });
      const imageHeaders = imageData[0];
      const images = imageData.slice(1).map(row => {
        const image = {};
        if (!isNaN(row[0]) && +row[0] !== 0) {
          row.forEach((cell, index) => {
            let key;
            switch (productHeaders[index]) {
              case "0":
                key = "ProductID";
                break;
              case "1":
                key = "ImgUrl1";
                break;
              case "2":
                key = "ImgUrl2";
                break;
              case "3":
                key = "ImgUrl3";
                break;
              case "4":
                key = "ImgUrl4";
                break;
              case "5":
                key = "ImgUrl5";
                break;
              case "6":
                key = "ImgUrl6";
                break;
              case "7":
                key = "ImgUrl7";
                break;
              case "8":
                key = "ImgUrl8";
                break;
              case "9":
                key = "ImgUrl9";
                break;
              case "10":
                key = "ImgUrl10";
                break;
              case "11":
                key = "ImgUrl11";
                break;
              case "12":
                key = "ImgUrl12";
                break;
              case "13":
                key = "ImgUrl13";
                break;
              case "14":
                key = "ImgUrl14";
                break;
              case "15":
                key = "ImgUrl15";
                break;
              case "16":
                key = "ImgUrl16";
                break;
              case "17":
                key = "ImgUrl17";
                break;
              case "18":
                key = "ImgUrl18";
                break;
              case "19":
                key = "ImgUrl19";
                break;
              case "20":
                key = "ImgUrl20";
                break;
              case "21":
                key = "ImgUrl21";
                break;
              case "22":
                key = "ImgUrl22";
                break;
              case "23":
                key = "ImgUrl23";
                break;
              default:
                key = productHeaders[index];
            }
            image[imageHeaders[index]] = cell;
          });
          return image;
        }
      });

      // Combine the data
      const decodedToken = jwt_decode(accessToken);
      const customerId = decodedToken["sub"];
      const validProducts = products.filter(product => product !== undefined);
      const validImages = images.filter(image => image !== undefined);
      validProducts.forEach((product, index) => {
        const filteredImages = validImages.filter(
          image => image.ProductID === product.ProductID
        );
        const imageUrls = [];
        filteredImages.forEach((images, index) => {
          const allUrls = [];
          for (const key in images) {
            if (key.startsWith("ImgUrl")) {
              const value = images[key];
              allUrls.push(value);
            }
          }
          imageUrls.push(allUrls);
        });

        product["OtherUrls"] = imageUrls[0];
        product["Id"] = `${customerId}-${product["ProductID"]}`;
        console.log(product["Id"]);
      });

      setItems(validProducts);
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div style={{ paddingTop: '16px' }}>
      <Typography>
        If you are a new seller, you can download the spreadsheet below, fill it in and then upload it here.
      </Typography>
      <Typography>
        <a
          href={`${process.env.PUBLIC_URL}/ProductUploadTemplate.xlsx`}
          download
        >
          Download Spreadsheet
        </a>
      </Typography>
      <Typography
        className={classes.typography}
        variant="span"
        component="span"
      >
        Either drag and drop a file in the space provided or click upload. Click
        Add products once uploaded
      </Typography>
      <Stack spacing={1}>
        <Dropzone
          onDrop={handleDrop}
          accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        >
          {({ getRootProps, getInputProps }) => (
            <div style={containerStyle} {...getRootProps()}>
              <input {...getInputProps()} />
              <div style={divStyle}>
                Drag and drop an excel file here or click
              </div>
            </div>
          )}
        </Dropzone>
      </Stack>
      <Button className={classes.button} variant="contained" component="label">
        {loadingFile && <CircularProgress />}
        Upload File
        <input type="file" hidden onChange={handleFileUpload} />
      </Button>
      <Button
        className={classes.button}
        variant="contained"
        onClick={handleSaveProductsClick}
      >
        {loading && <CircularProgress />}Add Products
      </Button>
      <Typography
        className={classes.typography}
        variant="span"
        component="span"
      >
        {loadingFile && <div>Loading the file and processing it</div>}
        {loading && <div>Uploading the data to the online system</div>}
      </Typography>
    </div>
  );
};

export default ExcelReader;
